// ./src/utils/api/GuestStayService.ts
//
// Guest stays (which accommodation a guest is at + check-in/out dates). OpResult-native,
// event-scoped. An ACCOMMODATION VIEW over the unified `event_guest_signup` table (which
// also backs signup-list / POI / travel sign-ups): this service always scopes to
// targetKind='accommodation' and maps the accommodation pointer + off-list name + dates
// to/from the table's generic columns (target_id / target_name / starts_on / ends_on), so
// callers never see the polymorphic columns. Talks to the eventGuestSignup* GraphQL ops.
// Mirrors PoiSignupService / SignupRecordService. See em-scaffold-ui.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { Pagination, SortItem } from "./commonApiTypes";

// This service always operates on accommodation targets in the unified table.
const ACCOMMODATION_TARGET_KIND = "accommodation";

// =============================================================================
// GraphQL
// =============================================================================

// Alias the unified table's polymorphic target + generic period columns back to the
// accommodation-shaped fields this service exposes (so consumers keep reading
// `record.accommodationId` / `.checkIn` / `.checkOut`).
const STAY_FIELDS = `
  id
  eventId
  accountId
  eventGuestId
  accommodationId: targetId
  name: targetName
  checkIn: startsOn
  checkOut: endsOn
  note
  orderNo
  status
  rights
  createdAt
  updatedAt
`;

const STAY_LIST_QUERY = gql`
  query EventGuestSignupList($filter: EventGuestSignupFilter, $params: PaginationAndSorting) {
    eventGuestSignupList(filter: $filter, params: $params) {
      code
      data { ${STAY_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const STAY_GET_QUERY = gql`
  query EventGuestSignupGet($id: ID) {
    eventGuestSignupGet(id: $id) {
      code
      data { ${STAY_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const STAY_GET_MANY_QUERY = gql`
  query EventGuestSignupGetMany($ids: [ID]) {
    eventGuestSignupGetMany(ids: $ids) {
      code
      data { ${STAY_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const STAY_CREATE_MUTATION = gql`
  mutation EventGuestSignupCreate($params: EventGuestSignupInput) {
    eventGuestSignupCreate(params: $params) {
      code
      data { ${STAY_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const STAY_UPDATE_MUTATION = gql`
  mutation EventGuestSignupUpdate($where: EventGuestSignupWhereInput, $params: EventGuestSignupInput) {
    eventGuestSignupUpdate(where: $where, params: $params) {
      code
      data { ${STAY_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const STAY_REMOVE_MUTATION = gql`
  mutation EventGuestSignupRemove($where: EventGuestSignupWhereInput) {
    eventGuestSignupRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types (the accommodation view; accommodationId → target_id, checkIn/out → starts/ends_on)
// =============================================================================

export interface GuestStay {
  id?: string;
  eventId?: string | null;
  accountId?: string | null;
  eventGuestId?: string | null;
  accommodationId?: string | null;
  name?: string | null;
  /** Check-in date, YYYY-MM-DD. */
  checkIn?: string | null;
  /** Check-out date, YYYY-MM-DD. */
  checkOut?: string | null;
  note?: string | null;
  orderNo?: number | null;
  status?: number;
  rights?: string[] | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface GuestStayInput {
  eventId?: string | null;
  eventGuestId?: string | null;
  accommodationId?: string | null;
  name?: string | null;
  checkIn?: string | null;
  checkOut?: string | null;
  note?: string | null;
  orderNo?: number | null;
  status?: number | null;
}

export interface GuestStayBulkInput {
  status?: number | null;
}

export interface GuestStayFilter {
  id?: string[] | null;
  eventId?: string[] | null;
  eventGuestId?: string[] | null;
  accommodationId?: string[] | null;
  status?: number[] | null;
}

export interface GuestStayWhereInput {
  id?: string | string[];
}

// Map the accommodation-scoped filter onto the unified table's generic filter:
// accommodation scope + accommodationId → targetId.
const toApiFilter = (filter?: GuestStayFilter): Record<string, unknown> => {
  const { accommodationId, ...rest } = filter || {};
  return {
    ...rest,
    targetKind: [ACCOMMODATION_TARGET_KIND],
    ...(accommodationId && accommodationId.length > 0 ? { targetId: accommodationId } : {}),
  };
};

// Map an accommodation-scoped create/update onto the generic input: accommodationId →
// target_kind + target_id, name → target_name, checkIn/checkOut → startsOn/endsOn.
const toApiInput = (params: GuestStayInput): Record<string, unknown> => {
  const { accommodationId, name, checkIn, checkOut, ...rest } = params;
  return {
    ...rest,
    ...(name !== undefined ? { targetName: name } : {}),
    ...(checkIn !== undefined ? { startsOn: checkIn } : {}),
    ...(checkOut !== undefined ? { endsOn: checkOut } : {}),
    targetKind: ACCOMMODATION_TARGET_KIND,
    ...(accommodationId !== undefined ? { targetId: accommodationId } : {}),
  };
};

// =============================================================================
// Service
// =============================================================================

export class GuestStayService {
  static async list(filter?: GuestStayFilter, pagination?: Pagination, sortBy?: SortItem[]): Promise<OpResult> {
    return ApiService.executeQuery(STAY_LIST_QUERY, {
      filter: toApiFilter(filter),
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(STAY_GET_QUERY, { id });
  }

  static async getMany(ids: string[]): Promise<OpResult> {
    return ApiService.executeQuery(STAY_GET_MANY_QUERY, { ids });
  }

  static async create(params: GuestStayInput): Promise<OpResult> {
    return ApiService.executeMutation(STAY_CREATE_MUTATION, { params: toApiInput(params) });
  }

  static async update(where: GuestStayWhereInput, params: GuestStayInput): Promise<OpResult> {
    return ApiService.executeMutation(STAY_UPDATE_MUTATION, { where, params: toApiInput(params) });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(STAY_REMOVE_MUTATION, { where: { id } });
  }

  static async bulkUpdate(ids: string[], params: GuestStayBulkInput): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(STAY_UPDATE_MUTATION, { where: { id: ids }, params });
  }

  static async removeMany(ids: string[]): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(STAY_REMOVE_MUTATION, { where: { id: ids } });
  }
}
